import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

/**
 * TripHeader - Header above HeroCard
 * 
 * Shows:
 * - Current time (toggleable: Local / Zulu / Base)
 * - Rotation number
 * 
 * Mobile-first, clean design
 */

const TripHeader = ({ rotationNumber, compiledData, timeZoneIndex, onToggleTimeZone }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format time based on zone
  const formatTime = (zone) => {
    if (zone === 0) { // Local
      return currentTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } else if (zone === 1) { // Zulu (UTC)
      return currentTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
    } else { // Base (get from meta)
      const base = compiledData?.compiled_json?.meta?.base || 'NYC';
      const baseTimezone = getBaseTimezone(base);
      return currentTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: baseTimezone
      });
    }
  };

  const getBaseTimezone = (base) => {
    const timezones = {
      'NYC': 'America/New_York',
      'JFK': 'America/New_York',
      'EWR': 'America/New_York',
      'LAX': 'America/Los_Angeles',
      'SFO': 'America/Los_Angeles',
      'ORD': 'America/Chicago',
      'DFW': 'America/Chicago',
      'ATL': 'America/New_York',
      'MIA': 'America/New_York',
      'SEA': 'America/Los_Angeles',
      'DEN': 'America/Denver',
      'PHX': 'America/Phoenix',
      'LAS': 'America/Los_Angeles',
    };
    return timezones[base] || 'America/New_York';
  };

  const getZoneLabel = (zone) => {
    if (zone === 0) return 'L'; // Local
    if (zone === 1) return 'Z'; // Zulu
    return 'B'; // Base
  };

  const getRotationDisplay = () => {
    if (rotationNumber) return rotationNumber;
    if (compiledData?.compiled_json?.meta?.rotation_number) {
      return compiledData.compiled_json.meta.rotation_number;
    }
    return '—';
  };

  const getDepartureInstant = () => {
    const firstLeg = compiledData?.compiled_json?.duty_periods?.[0]?.legs?.[0];
    const utc = firstLeg?.sched_out?.utc;
    const localWall = firstLeg?.sched_out?.local_wall;
    const iso = utc || localWall;
    return iso ? new Date(iso) : null;
  };

  const getActualOutInstant = () => {
    const firstLeg = compiledData?.compiled_json?.duty_periods?.[0]?.legs?.[0];
    const utc = firstLeg?.actual_out?.utc;
    const localWall = firstLeg?.actual_out?.local_wall;
    const iso = utc || localWall;
    return iso ? new Date(iso) : null;
  };

  const formatCountdown = (totalSeconds) => {
    const pad = (num) => String(num).padStart(2, '0');
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const formatSignedCountdown = (seconds) => {
    const sign = seconds < 0 ? '-' : '';
    return `${sign}${formatCountdown(Math.abs(seconds))}`;
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg overflow-hidden">
      {/* Header content */}
      <div className="p-4 grid grid-cols-3 items-center">
        
        {/* Left: Time with icon */}
        <div className="justify-self-start">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:bg-slate-700/50 rounded-lg p-2 transition-colors"
            onClick={onToggleTimeZone}
            title="Click to toggle: Local → Zulu → Base"
          >
            <Clock className="w-6 h-6 text-cyan-400" />
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wider">Current Time</div>
              <div className="text-white font-mono text-2xl font-bold">
                {formatTime(timeZoneIndex)}<span className="text-cyan-400 ml-1">{getZoneLabel(timeZoneIndex)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Center: Departure countdown */}
        <div className="text-center">
          <div className="text-gray-400 text-xs uppercase tracking-wider">Departure Countdown</div>
          {(() => {
            const departureInstant = getDepartureInstant();
            if (!departureInstant || Number.isNaN(departureInstant.getTime())) {
              return <div className="text-white font-mono text-2xl font-bold">--:--:--</div>;
            }

            const actualOutInstant = getActualOutInstant();
            if (actualOutInstant && !Number.isNaN(actualOutInstant.getTime())) {
              const frozenDiffSeconds = Math.floor((actualOutInstant.getTime() - departureInstant.getTime()) / 1000);
              const label = frozenDiffSeconds >= 0 ? 'Out +' : 'Out -';
              return (
                <div className="text-amber-400 font-mono text-2xl font-bold">
                  {label}{formatCountdown(Math.abs(frozenDiffSeconds))}
                </div>
              );
            }

            const diffSeconds = Math.floor((departureInstant.getTime() - currentTime.getTime()) / 1000);
            if (diffSeconds >= 0) {
              return (
                <div className="text-white font-mono text-2xl font-bold">
                  T-{formatCountdown(diffSeconds)}
                </div>
              );
            }

            return (
              <div className="text-orange-400 font-mono text-2xl font-bold">
                Past Due +{formatCountdown(Math.abs(diffSeconds))}
              </div>
            );
          })()}
        </div>

        {/* Right: Rotation number */}
        <div className="text-right justify-self-end">
          <div className="text-gray-400 text-xs uppercase tracking-wider">Rotation</div>
          <div className="text-white font-semibold text-2xl">
            {getRotationDisplay()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default TripHeader;
